'use client';

import { useTranslations } from 'next-intl';
import type { FallbackProps } from 'react-error-boundary';

import { Button } from '@/components/button';
import SupportTicketLink from '@/components/support/SupportTicketLink';

const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const t = useTranslations('errors.fallback');
  const errorInstance = error instanceof Error ? error : new Error(String(error));

  const handleReload = () => {
    if (globalThis.window !== undefined) {
      globalThis.window.location.reload();
    }
  };

  return (
    <div className="flex flex-1 items-center justify-center p-6 bg-surface-primary">
      <div className="max-w-lg w-full">
        <div className="bg-surface-secondary rounded-lg p-6 border border-border-primary">
          <div className="text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h1 className="text-xl font-medium text-text-primary mb-2">{t('title')}</h1>
            <p className="text-text-secondary mb-6">{t('description')}</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <Button onClick={resetErrorBoundary} variant="primary" className="flex-1">
                {t('tryAgain')}
              </Button>
              <Button onClick={handleReload} variant="secondary" className="flex-1">
                {t('reload')}
              </Button>
            </div>
            <div className="mt-3">
              <SupportTicketLink />
            </div>
            {/* Show error details in development */}
            {process.env.NEXT_PUBLIC_NODE_ENV === 'development' && errorInstance && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-text-secondary hover:text-text-primary">
                  🔍 Error Details (Development)
                </summary>
                <div className="mt-3 p-3 bg-surface-tertiary border border-red-500/20 rounded-sm text-xs font-mono text-text-primary overflow-y-auto max-h-60">
                  <div className="mb-2">
                    <strong>Error:</strong> {errorInstance.message}
                  </div>
                  {errorInstance.stack && (
                    <div>
                      <strong>Stack:</strong>
                      <pre className="whitespace-pre-wrap">{errorInstance.stack}</pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
